import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaInsuranceVerificationAdapterV2Service, serviceName } from '../service';
import { GetPayerMixAnalysisIO, ServiceQueries } from '../types';
import { useInsuranceVerificationAdapterV2Queries } from './use-insurance-verification-adapter-v2-queries';
import {
  UseInsuranceVerificationAdapterV2QueryEndpointArgs,
  useInsuranceVerificationAdapterV2Query,
} from './use-insurance-verification-adapter-v2-query';

/**
 * A hook that returns a query for the `GetPayerMixAnalysis` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPayerMixAnalysisQuery = <E = unknown, D = GetPayerMixAnalysisIO['output']>(
  args: UseInsuranceVerificationAdapterV2QueryEndpointArgs<'GetPayerMixAnalysis', E, D>
) =>
  useInsuranceVerificationAdapterV2Query({
    endpointName: 'GetPayerMixAnalysis',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetPayerMixAnalysis` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetPayerMixAnalysisQueries = <E = unknown, D = GetPayerMixAnalysisIO['output']>(
  argsArr: UseInsuranceVerificationAdapterV2QueryEndpointArgs<'GetPayerMixAnalysis', E, D>[]
) =>
  useInsuranceVerificationAdapterV2Queries(
    argsArr.map((args) => ({
      endpointName: 'GetPayerMixAnalysis',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetPayerMixAnalysis` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetPayerMixAnalysis = () => {
  const queryClient = useQueryClient();

  const fetchGetPayerMixAnalysis = async <E = unknown, D = GetPayerMixAnalysisIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseInsuranceVerificationAdapterV2QueryEndpointArgs<'GetPayerMixAnalysis', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetPayerMixAnalysisIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPayerMixAnalysis'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetPayerMixAnalysisIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetPayerMixAnalysis'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetPayerMixAnalysis'>({
          serviceName,
          endpointName: 'GetPayerMixAnalysis',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaInsuranceVerificationAdapterV2Service.GetPayerMixAnalysis(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetPayerMixAnalysis;
};

/**
 * Invalidates the cached `GetPayerMixAnalysis` query for a specific request.
 */
export const useInvalidateGetPayerMixAnalysisQuery = () => {
  const queryClient = useQueryClient();

  return async (request: GetPayerMixAnalysisIO['input']) => {
    await queryClient.invalidateQueries(
      getSchemaQueryKey<ServiceQueries, 'GetPayerMixAnalysis'>({
        serviceName,
        endpointName: 'GetPayerMixAnalysis',
        request,
      }),
      { exact: true }
    );
  };
};
